"use client"

export const dynamic = 'force-dynamic'

import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Slider } from "@/components/ui/slider"
import { Button } from "@/components/ui/button"
import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"
import { DollarSign, Calculator, TrendingDown, Users, FileText, Key } from "lucide-react"
import Link from "next/link"

export default function PriceTestPage() {
  const [reviewers, setReviewers] = useState([5])
  const [words, setWords] = useState([50000])
  const [translationKeys, setTranslationKeys] = useState([5000])

  // Pricing calculations
  const calculateLocsyPrice = () => {
    const platformFee = 20 // $20/month
    const openAiCost = Math.ceil(words[0] / 1000) * 0.01 // Rough estimate: $0.01 per 1K words
    return platformFee + openAiCost
  }

  const calculateCrowdinPrice = () => {
    const basePrice = 150 // Team plan base
    const additionalUsers = Math.max(0, reviewers[0] - 5)
    const userCost = additionalUsers * 10 // $10 per additional user
    const wordTier = words[0] > 100000 ? 100 : words[0] > 50000 ? 50 : 0
    return basePrice + userCost + wordTier
  }

  const calculateLokalisePrice = () => {
    const basePrice = 140 // Start plan (10 users)
    const additionalUsers = Math.max(0, reviewers[0] - 10)
    const userCost = additionalUsers * 14 // $14 per additional user
    const keyOverage = Math.max(0, translationKeys[0] - 5000)
    const keyCost = Math.ceil(keyOverage / 1000) * 20 // $20 per 1K keys over limit
    return basePrice + userCost + keyCost
  }

  const locsyPrice = calculateLocsyPrice()
  const crowdinPrice = calculateCrowdinPrice()
  const lokalisePrice = calculateLokalisePrice()

  const savingsVsCrowdin = crowdinPrice - locsyPrice
  const savingsVsLokalise = lokalisePrice - locsyPrice

  return (
    <div className="min-h-screen">
      <Navigation />
      
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">
              <Calculator className="w-3 h-3 mr-1" />
              Pricing Calculator
            </Badge>
            <h1 className="text-4xl font-bold mb-4">
              Calculate Your Localization Savings
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Compare real-world costs between Locsy, Crowdin, and Lokalise based on your team size and usage.
            </p>
          </div>

          {/* Calculator Controls */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Users className="h-5 w-5 mr-2" />
                  Team Size
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Reviewers</span>
                    <span className="font-medium">{reviewers[0]}</span>
                  </div>
                  <Slider
                    value={reviewers}
                    onValueChange={setReviewers}
                    max={50}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1</span>
                    <span>50</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Content Volume
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Words</span>
                    <span className="font-medium">{words[0].toLocaleString()}</span>
                  </div>
                  <Slider
                    value={words}
                    onValueChange={setWords}
                    max={200000}
                    min={1000}
                    step={1000}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1K</span>
                    <span>200K</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Key className="h-5 w-5 mr-2" />
                  Translation Keys
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Keys</span>
                    <span className="font-medium">{translationKeys[0].toLocaleString()}</span>
                  </div>
                  <Slider
                    value={translationKeys}
                    onValueChange={setTranslationKeys}
                    max={20000}
                    min={100}
                    step={100}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>100</span>
                    <span>20K</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Comparison */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Locsy */}
            <Card className="border-2 border-primary">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Locsy</span>
                  <Badge variant="secondary">Best Value</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-primary mb-4">
                  ${locsyPrice.toFixed(0)}
                  <span className="text-sm font-normal text-muted-foreground">/month</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Platform fee</span>
                    <span>$20</span>
                  </div>
                  <div className="flex justify-between">
                    <span>OpenAI costs</span>
                    <span>${(locsyPrice - 20).toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Team members</span>
                    <span className="text-primary">Unlimited</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Translation keys</span>
                    <span className="text-primary">Unlimited</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Crowdin */}
            <Card>
              <CardHeader>
                <CardTitle>Crowdin</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600 mb-4">
                  ${crowdinPrice.toFixed(0)}
                  <span className="text-sm font-normal text-muted-foreground">/month</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Team plan base</span>
                    <span>$150</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Additional users</span>
                    <span>${Math.max(0, reviewers[0] - 5) * 10}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Word tier upgrade</span>
                    <span>${words[0] > 100000 ? 100 : words[0] > 50000 ? 50 : 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Limited features</span>
                    <span className="text-orange-600">Per tier</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Lokalise */}
            <Card>
              <CardHeader>
                <CardTitle>Lokalise</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-orange-600 mb-4">
                  ${lokalisePrice.toFixed(0)}
                  <span className="text-sm font-normal text-muted-foreground">/month</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Start plan (10 users)</span>
                    <span>$140</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Additional users</span>
                    <span>${Math.max(0, reviewers[0] - 10) * 14}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Key overages</span>
                    <span>${Math.ceil(Math.max(0, translationKeys[0] - 5000) / 1000) * 20}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>5K key limit</span>
                    <span className="text-orange-600">Restrictive</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Savings Summary */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-2 border-green-500">
              <CardHeader>
                <CardTitle className="flex items-center text-green-600">
                  <TrendingDown className="h-5 w-5 mr-2" />
                  Savings vs Crowdin
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600 mb-2">
                  ${savingsVsCrowdin.toFixed(0)}/month
                </div>
                <div className="text-sm text-muted-foreground">
                  Annual savings: ${(savingsVsCrowdin * 12).toFixed(0)}
                </div>
                <div className="text-sm text-green-600 font-medium">
                  {((savingsVsCrowdin / crowdinPrice) * 100).toFixed(0)}% cost reduction
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-500">
              <CardHeader>
                <CardTitle className="flex items-center text-green-600">
                  <TrendingDown className="h-5 w-5 mr-2" />
                  Savings vs Lokalise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600 mb-2">
                  ${savingsVsLokalise.toFixed(0)}/month
                </div>
                <div className="text-sm text-muted-foreground">
                  Annual savings: ${(savingsVsLokalise * 12).toFixed(0)}
                </div>
                <div className="text-sm text-green-600 font-medium">
                  {((savingsVsLokalise / lokalisePrice) * 100).toFixed(0)}% cost reduction
                </div>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Card className="p-8 border-2 border-primary">
              <h3 className="text-2xl font-bold mb-4">Ready to Save up to ${Math.max(savingsVsCrowdin, savingsVsLokalise).toFixed(0)}/month?</h3>
              <p className="text-muted-foreground mb-6">
                Start your free trial and see how much you can save on localization costs.
              </p>
              <Button size="lg" className="mr-4" asChild>
                <Link href="/auth/signup">Start Free Trial</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link href="/pricing">View Full Pricing</Link>
              </Button>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}